import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, Info, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { StatusChip } from "@/components/app/StatusChip";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { A_STATUS } from "@/lib/assessments";
import { ReliabilityBadge } from "@/pages/candidates/assessment/ReliabilityBadge";
import { BigFiveRadar } from "@/pages/candidates/assessment/BigFiveRadar";
import { WorkStyleBars } from "@/pages/candidates/assessment/WorkStyleBars";
import { StyleMap } from "@/pages/candidates/assessment/StyleMap";
import { AlignmentBreakdown } from "@/pages/candidates/assessment/AlignmentBreakdown";
import { NarrativePanel, SjtResults, ValidityPanel } from "@/pages/candidates/assessment/AssessmentPanels";
import { ExtraResults, PlanCard } from "@/pages/candidates/assessment/ExtraPanels";

function Results({ data, refresh }) {
  const { user } = useAuth();
  const a = data.assessment;
  const r = a.results;
  const retake = async () => { try { await api.post(`/assessments/${a.id}/retake`); toast.success("Retake offered — a new link was emailed"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  if (r.personality_included === false) return (
    <div className="space-y-6" data-testid="assessment-results">
      <p className="panel p-5 text-sm text-ink-secondary" data-testid="no-personality-note">This assessment had no Personality section, so only scenario and question results are shown.</p>
      <SjtResults sjt={r.sjt} />
      <ExtraResults a={a} />
    </div>
  );
  return (
    <div className="space-y-6" data-testid="assessment-results">
      <div className="panel flex flex-wrap items-center gap-3 p-5">
        <ReliabilityBadge level={r.validity.level} reasons={r.validity.reasons} testId="reliability-badge" />
        <span className="text-sm text-ink-secondary">{{ ipip_50: "IPIP-50", ipip_neo_120_safe: "IPIP-NEO-120 (hiring-safe)", ipip_neo_120: "IPIP-NEO-120 (full research version)" }[r.instrument]} · completed {new Date(a.completed_at).toLocaleDateString()}</span>
        {r.hiring_safe && r.instrument !== "ipip_50" && <span className="inline-flex items-center gap-1 text-xs text-ink-muted" title="Political and religious statements were removed. Liberalism (O6) is not scored; Openness uses the other 5 facets with adjusted norms." data-testid="hiring-safe-info"><Info size={12} aria-hidden="true" /> Hiring-safe version: political/religious items removed</span>}
        {r.validity.level === "low" && STAFF.includes(user.role) && !a.retake_offered && <Button size="sm" variant="outline" className="ml-auto" onClick={retake} data-testid="offer-retake-btn"><RotateCcw size={14} /> Offer retake</Button>}
        {a.retake_offered && <span className="ml-auto text-xs text-ink-muted">Retake offered</span>}
        <p className="w-full text-xs italic text-ink-muted" data-testid="indicator-note">Personality results are indicators, not verdicts.</p>
      </div>
      {r.validity.level === "low" && <p className="rounded-[6px] border border-[#E8C9C5] bg-risk-tint px-4 py-3 text-sm text-risk" data-testid="low-reliability-warning">These answers may not reflect this person well ({r.validity.reasons.join("; ")}). Treat the scores below with caution or offer a retake.</p>}
      <div className="grid gap-6 lg:grid-cols-2">
        <BigFiveRadar pcts={r.percentiles} ideal={data.ideal} />
        <AlignmentBreakdown alignment={r.alignment} />
        <WorkStyleBars indices={r.indices} ideal={data.ideal} />
        <div className="space-y-6"><StyleMap style={r.style} /><ValidityPanel a={a} /></div>
      </div>
      <NarrativePanel a={a} />
      <SjtResults sjt={r.sjt} />
      <ExtraResults a={a} />
    </div>
  );
}

function ForApplication({ appId }) {
  const qc = useQueryClient();
  const key = ["app-assessment", appId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => get(`/applications/${appId}/assessment`),
    refetchInterval: (q) => (q.state.data?.assessment?.narrative_status === "queued" ? 4000 : false) });
  if (isLoading) return <SkeletonRows rows={6} />;
  const a = data.assessment;
  if (!a) return <div className="space-y-4"><EmptyState testId="assessment-empty" icon={ClipboardCheck} title="No assessment yet" text={`Send it from the ${data.job_title} Assess step once this candidate is shortlisted.`} /><PlanCard plan={data.plan} /></div>;
  if (a.status !== "completed") {
    return (
      <div className="space-y-4"><div className="panel flex flex-wrap items-center gap-3 p-6" data-testid="assessment-pending">
        <StatusChip status={A_STATUS[a.status]} />
        <p className="text-sm text-ink-secondary">Sent {new Date(a.sent_at).toLocaleDateString()} · {a.expires_at ? `${a.status === "expired" ? "expired" : "expires"} ${new Date(a.expires_at).toLocaleString()}` : "Link doesn't expire · single use"}</p>
      </div><PlanCard plan={data.plan} sent /></div>
    );
  }
  return <Results data={data} refresh={() => qc.invalidateQueries({ queryKey: key })} />;
}

export function AssessmentTab({ c }) {
  const [appId, setAppId] = useState(c.applications[0]?.id);
  if (!c.applications.length) return <EmptyState icon={ClipboardCheck} title="No applications" text="This candidate isn't linked to a job yet." />;
  return (
    <div className="space-y-4" data-testid="candidate-assessment-tab">
      {c.applications.length > 1 && (
        <select value={appId} onChange={(e) => setAppId(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2 text-sm" data-testid="assessment-app-select">
          {c.applications.map((a) => <option key={a.id} value={a.id}>{a.job_title}</option>)}
        </select>
      )}
      <ForApplication key={appId} appId={appId} />
    </div>
  );
}
